import { detectEnvironment } from "@zektec/environment-utils";

const DEFAULT_STAGE_INDICATORS = [
  "stage",
  "staging",
  "release-stage",
  "pages.dev",
];
const DEFAULT_LOCAL_INDICATORS = ["localhost", ".local", "127.0.0.1"];

export type Placement = "bottom-right" | "bottom-left" | "bottom-center";

export type EnvironmentTagModelOptions = {
  environment?: string;
  isStaging?: boolean;
  isLocal?: boolean;
  origin?: string;
  stageIndicators?: string[];
  localIndicators?: string[];
  showInProduction?: boolean;
  version?: string | null;
  placement?: Placement;
  offset?: number;
  zIndex?: number;
};

function detectFromText(
  value: string | undefined,
  indicators: string[],
): boolean {
  if (!value) {
    return false;
  }

  const normalized = value.toLowerCase();
  return indicators.some((indicator) =>
    normalized.includes(indicator.toLowerCase()),
  );
}

function resolveLabels({
  environment,
  origin,
  stageIndicators,
  localIndicators,
  isStaging,
  isLocal,
}: Pick<
  EnvironmentTagModelOptions,
  | "environment"
  | "origin"
  | "stageIndicators"
  | "localIndicators"
  | "isStaging"
  | "isLocal"
>): string[] {
  const labels: string[] = [];
  const detectedEnvironment = detectEnvironment({
    ...(environment === undefined ? {} : { mode: environment }),
    ...(origin === undefined ? {} : { origin }),
    ...(stageIndicators === undefined
      ? {}
      : { stagingIndicators: stageIndicators }),
    ...(localIndicators === undefined
      ? {}
      : { localHostIndicators: localIndicators }),
  });

  const shouldShowStaging =
    Boolean(isStaging) ||
    detectedEnvironment === "staging" ||
    detectFromText(origin, stageIndicators || DEFAULT_STAGE_INDICATORS);

  const shouldShowLocal =
    Boolean(isLocal) ||
    detectedEnvironment === "local" ||
    detectedEnvironment === "development" ||
    detectedEnvironment === "test" ||
    detectFromText(origin, localIndicators || DEFAULT_LOCAL_INDICATORS);

  if (shouldShowStaging) {
    labels.push("STAGING");
  }

  if (shouldShowLocal) {
    labels.push("LOCAL");
  }

  return labels;
}

export function placementStyle(
  placement: Placement,
  offset: number,
): Record<string, string | number> {
  if (placement === "bottom-left") {
    return { left: offset, bottom: offset };
  }

  if (placement === "bottom-center") {
    return {
      left: "50%",
      bottom: offset,
      transform: "translateX(-50%)",
    };
  }

  return { right: offset, bottom: offset };
}

export function resolveEnvironmentTag(
  options: EnvironmentTagModelOptions = {},
) {
  const labels = resolveLabels(options);
  const normalizedVersion = options.version?.trim();
  const text = labels.length ? labels.join(" + ") : "NON-PRODUCTION";
  return {
    visible: Boolean(options.showInProduction) || labels.length > 0,
    text: normalizedVersion ? `${text} v${normalizedVersion}` : text,
    style: {
      position: "fixed",
      ...placementStyle(
        options.placement ?? "bottom-right",
        options.offset ?? 12,
      ),
      zIndex: options.zIndex ?? 9999,
      fontFamily:
        "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      color: "#7a4300",
      backgroundColor: "#fff4d6",
      border: "1px solid #f4c06b",
      borderRadius: 9999,
      padding: "6px 10px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
      pointerEvents: "none",
    },
  };
}

// Match React's numeric CSS serialization in the Astro adapter.
const unitless = new Set(["zIndex", "fontWeight"]);
export function serializeTagStyle(
  style: Record<string, string | number>,
): string {
  return Object.entries(style)
    .map(([key, value]) => {
      const property = key.replace(
        /[A-Z]/g,
        (letter) => `-${letter.toLowerCase()}`,
      );
      const cssValue =
        typeof value === "number" && !unitless.has(key) ? `${value}px` : value;
      return `${property}: ${cssValue}`;
    })
    .join("; ");
}
